import {
  EDesignerEntityKind,
  EMailTemplateType,
  ENotificationTemplateType,
  ESdChangeReason,
  INotificationTemplateDto,
  ViewPart
} from "@kuika/kuika-cl-model";
import { ColumnSort, OnChangeFn, SortingState } from "@tanstack/react-table";
import * as React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { NAME_COLLATOR } from "../../../shared/utility/name-collator";
import {
  hasRecentlyModified,
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  resolveActiveTab,
  selectRecentlyModified
} from "../../../shared/utility/recently-modified";
import { buildLastModifiedColumn } from "../../../shared/utility/recently-modified-columns";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaFilterChips, { IKuikaFilterChipOption } from "../../ui-kit/kuika-filter-chips/kuika-filter-chips";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell,
  KuikaListTableUserCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTemplateCard, {
  KuikaTemplateCardGrid,
  KuikaTemplateCardSkeleton
} from "../../ui-kit/kuika-template-card/kuika-template-card";
import KuikaH4 from "../../ui-kit/typography/H4/H4";
import { EmailTemplateLanguagesCell } from "./email-template-languages-cell";
import { EmailTemplateNameModal } from "./email-template-name-modal";
import { buildEmailTemplateRowActions } from "./email-template-row-actions";
import {
  EMAIL_TEMPLATE_CATALOG,
  emailTemplateCategories,
  filterEmailTemplates,
  IEmailTemplateCatalogItem,
  sortEmailTemplates
} from "./email-templates";

/** What the Sort-by control and the sortable headers can be set to, one key for both. */
type EmailTemplateSortKey =
  | "name-asc"
  | "name-desc"
  | "created-desc"
  | "created-asc"
  | "type-asc"
  | "type-desc"
  | "languages-asc"
  | "languages-desc";

/** The headers and the Sort-by control are one state; this says which column shows a key. */
const SORT_COLUMN_BY_KEY: Record<EmailTemplateSortKey, string> = {
  "name-asc": "name",
  "name-desc": "name",
  "created-desc": "created",
  "created-asc": "created",
  "type-asc": "role",
  "type-desc": "role",
  "languages-asc": "languages",
  "languages-desc": "languages"
};

const SORT_OPTIONS: KuikaSelectboxOption[] = [
  { value: "created-desc", label: "Newest first" },
  { value: "created-asc", label: "Oldest first" },
  { value: "name-asc", label: "Name (A\u2013Z)" },
  { value: "name-desc", label: "Name (Z\u2013A)" },
  { value: "type-asc", label: "Type (A\u2013Z)" },
  { value: "type-desc", label: "Type (Z\u2013A)" },
  // The column counts a template's languages: a single-language one counts none.
  { value: "languages-asc", label: "Single language first" },
  { value: "languages-desc", label: "Most languages first" }
];

/**
 * The Email Builder landing page: the project's email templates as a table, with the free
 * starter catalog behind a second tab.
 *
 * It replaces the Emails LEFT PANEL, so everything that panel could do lives here — the
 * list, its search (and the search's persistence), duplicate, delete, the "no actions on a
 * system template" rule, both empty states, and the unsaved-changes handshake on opening a
 * template. Templates sit in a tab rather than a sub-page for the same reason Process
 * Automation puts its process templates there: they are ready-made starting points for the
 * very entity the table lists.
 */

const EMAILS_TAB: string = "Emails";
const TEMPLATES_TAB: string = "Templates";
/** How many catalog cards to paint before "Load more"; 422 at once is a CDN stampede. */
const TEMPLATE_PAGE_SIZE: number = 24;

export interface IEmailBuilderLandingProps {
  /** True while the canvas has a create in flight, so the cards can block a second click. */
  isCreating: boolean;
  /**
   * Where the create stands, for the name sheet's confirm. The canvas opens the new template's
   * editor once the ✓ has been seen, and that editor replaces this page — the sheet goes with it.
   * A failed create leaves the sheet open on "Try again".
   */
  createStatus: KuikaButtonStatus;
  /** Id of the row whose duplicate is in flight; its actions trigger shows a spinner. */
  duplicatingId?: string;
  /** Starts the create; `createStatus` reports how it goes. */
  onCreateBlank: (name: string) => void;
  onCreateFromTemplate: (templateId: string, name: string) => void;
  /**
   * The template an UPDATE has just saved. The editor closes onto this list, and the row the
   * save changed is where the reader's eye is taken — the confirmation the toast used to be.
   */
  revealTemplateId?: string | null;
  onTemplateRevealed?: () => void;
  /** Every mutation belongs to the canvas, which stays mounted while the editor is open. */
  onOpenTemplate: (template?: INotificationTemplateDto) => void;
  onDuplicate: (template: INotificationTemplateDto) => void;
  onRequestDelete: (template: INotificationTemplateDto) => void;
}

interface IEmailBuilderLandingState {
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
  searchText: string;
  templatePageSize: number;
  /** Selected template categories. Empty means every category. */
  selectedCategories: string[];
  /** Null is the list's own order: the system templates first, then by name. */
  sort: EmailTemplateSortKey | null;
  pendingTemplateId?: string;
  isNameModalVisible: boolean;
}

class EmailBuilderLanding extends PureSdComponent<IEmailBuilderLandingProps & IWithSDProps, IEmailBuilderLandingState> {
  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: IEmailBuilderLandingProps & IWithSDProps) {
    super(props);
    this.state = {
      pickedTab: null,
      feed: LOADING_FEED,
      searchText: "",
      templatePageSize: TEMPLATE_PAGE_SIZE,
      selectedCategories: [],
      sort: null,
      pendingTemplateId: undefined,
      isNameModalVisible: false
    };
  }

  /**
   * The refresh the left panel used to do on mount, and it is not merely a nicety:
   * `ProjectProvider.loadNotificationTemplates` returns early for sessions flagged
   * `FROM_AI_SCREEN_GENERATION`, and this is what repairs the empty list — which the Send
   * Mail action's template picker and the workflow mail step both read.
   */
  componentDidMount = (): void => {
    this.isMounted_ = true;
    this.props.sd.getNotificationTemplates();
    const searchText: string = this.props.sd.searchTexts[ViewPart.NotificationTemplatePanel];
    if (searchText && searchText.length > 0) {
      this.setState({ searchText });
    }
    void this.loadFeed();
  };

  componentWillUnmount = (): void => {
    this.isMounted_ = false;
  };

  /**
   * The page stays mounted while a create or a duplicate lands (the sheet's ✓ is shown here, and
   * a duplicate never leaves the page at all), so `notificationTemplates` can gain or lose a row
   * without this page ever remounting to re-fetch the feed on its own. The id set is the smallest
   * correct signal: it fires on a create/duplicate/delete and stays quiet on an in-place rename,
   * which this page reads off the row itself rather than off the feed.
   */
  componentDidUpdate = (prevProps: Readonly<IEmailBuilderLandingProps & IWithSDProps>): void => {
    if (
      haveItemIdsChanged(
        this.getEmailTemplates(prevProps),
        this.getEmailTemplates(),
        (row: INotificationTemplateDto) => row.id
      )
    ) {
      void this.loadFeed();
    }
  };

  /**
   * `INotificationTemplateDto` carries who CREATED a template and when, but nothing about the
   * edits since — when one was last touched can only come from the entity-modification feed.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const projectId: string | undefined = this.props.sd?.project?.id;
    const feed: IModificationFeed = await loadModificationFeed([EDesignerEntityKind.NotificationTemplate], projectId);
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  /** The recent tab lists email templates, the same rows the Emails tab does — never the catalog. */
  getRecentTemplates = (): INotificationTemplateDto[] =>
    selectRecentlyModified<INotificationTemplateDto>({
      items: this.getFilteredTemplates(),
      index: this.state.feed.index,
      getId: (row: INotificationTemplateDto) => row.id,
      getName: (row: INotificationTemplateDto) => row.name ?? "",
      now: Date.now()
    });

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to the Emails list when nothing is recent or the feed failed, and otherwise
   * shows whatever the user picked.
   */
  getActiveTab = (): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      // Asked of every email template, not the searched ones: a query that matches nothing recent
      // must not move the user to another tab mid-keystroke.
      hasRecent: hasRecentlyModified<INotificationTemplateDto>(
        this.getEmailTemplates(),
        this.state.feed.index,
        (row: INotificationTemplateDto) => row.id,
        Date.now()
      ),
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: EMAILS_TAB
    });

  handleSearchChange = (searchText: string): void => {
    this.setState({ searchText, templatePageSize: TEMPLATE_PAGE_SIZE });
    // Same store key the panel used, so a query survives leaving the tool and coming back.
    this.props.sd.setSearchTexts(ViewPart.NotificationTemplatePanel, searchText);
  };

  /**
   * Takes `props` explicitly, defaulting to the current ones, so `componentDidUpdate` can ask the
   * same question of `prevProps` without a second, drifting copy of this filter.
   */
  getEmailTemplates = (props: IEmailBuilderLandingProps & IWithSDProps = this.props): INotificationTemplateDto[] =>
    (props.sd?.notificationTemplates ?? []).filter(
      (item: INotificationTemplateDto): boolean => item.templateType === ENotificationTemplateType.Email
    );

  /**
   * Matching is reproduced from the panel verbatim, transliteration quirk included: the
   * QUERY is transliterated, the candidate name is not. Changing it would silently change
   * which templates a query surfaces.
   */
  getFilteredTemplates = (): INotificationTemplateDto[] => {
    const searchText: string = this.state.searchText;
    const matches: INotificationTemplateDto[] = this.getEmailTemplates().filter(
      (x: INotificationTemplateDto): boolean =>
        x.name.toLowerCase().indexOf(KMainFunctions.turkishCharsToEnglish(searchText).toLowerCase()) !== -1
    );

    const sort: EmailTemplateSortKey | null = this.state.sort;
    // Untouched, the list keeps the order it has always had: the system templates first, then
    // by name. A chosen sort means the whole column, system or not.
    if (!sort) return sortEmailTemplates(matches);

    const isDescending: boolean = sort.endsWith("-desc");
    return [...matches].sort((a: INotificationTemplateDto, b: INotificationTemplateDto): number => {
      const primary: number = this.compareBySortColumn(a, b, sort);
      if (primary !== 0) return isDescending ? -primary : primary;
      // Deliberately outside the flip: descending reverses the column that was asked for, not
      // the order the equals settle into.
      const byCreated: number = this.getCreatedTimestamp(b) - this.getCreatedTimestamp(a);
      return byCreated !== 0 ? byCreated : NAME_COLLATOR.compare(a.name, b.name);
    });
  };

  getCreatedTimestamp = (template: INotificationTemplateDto): number =>
    template.createdAt ? new Date(template.createdAt).getTime() : 0;

  /** Ascending by the chosen column; the caller flips it and settles the equals. */
  compareBySortColumn = (
    a: INotificationTemplateDto,
    b: INotificationTemplateDto,
    sort: EmailTemplateSortKey
  ): number => {
    if (sort.startsWith("name")) return NAME_COLLATOR.compare(a.name, b.name);
    if (sort.startsWith("created")) return this.getCreatedTimestamp(a) - this.getCreatedTimestamp(b);
    if (sort.startsWith("languages")) return this.getLanguageCount(a) - this.getLanguageCount(b);
    // The label rather than the enum: the column shows "Email Verification", so "Type (A–Z)"
    // has to mean the words the reader can see.
    return this.getTemplateStatusLabel(a.templateStatus).localeCompare(this.getTemplateStatusLabel(b.templateStatus));
  };

  getLanguageCount = (template: INotificationTemplateDto): number =>
    template.multiLanguageEnabled ? (template.languageTemplates?.length ?? 0) : 0;

  /** The Sort-by choice as tanstack states it, so the matching header carries the arrow. */
  getSortingState = (): SortingState => {
    const sort: EmailTemplateSortKey | null = this.state.sort;
    if (!sort) return [];
    return [{ id: SORT_COLUMN_BY_KEY[sort], desc: sort.endsWith("-desc") }];
  };

  /** A header click writes the state the Sort-by control writes, so the two never disagree. */
  handleSortingChange: OnChangeFn<SortingState> = (updater): void => {
    const next: SortingState = typeof updater === "function" ? updater(this.getSortingState()) : updater;
    const entry: ColumnSort | undefined = next[0];
    // Third click clears the sort; the list falls back to its own order.
    if (!entry) {
      this.setState({ sort: null });
      return;
    }
    // The Type column is `role` for historical reasons; the sort key says what it shows.
    const key: string = entry.id === "role" ? "type" : entry.id;
    this.setState({ sort: `${key}-${entry.desc ? "desc" : "asc"}` as EmailTemplateSortKey });
  };

  getColumns = (): KuikaListTableColumn<INotificationTemplateDto>[] => [
    {
      // Declared, and the widest: an undeclared column falls back to tanstack's 150, which left
      // the row's longest content narrower than its Created date and every long name cut to
      // "Cok Uzun Bir S…". The table always fills its width, so these are ratios rather than
      // pixels — the data sources list's 380 belongs to a shorter row and left this one's Name
      // a half-empty gutter.
      id: "name",
      header: () => "Name",
      width: 260,
      accessorFn: (row: INotificationTemplateDto) => row.name?.toLowerCase() ?? "",
      cell: (row: INotificationTemplateDto) => (
        <span
          className="kuika-email-builder__name-cell"
          data-testid={`${row.name}Template`}
          style={
            {
              "--kuika-list-table-icon-bg": "var(--blue-1)",
              "--kuika-list-table-icon-color": "var(--accent-fg)"
            } as React.CSSProperties
          }
        >
          <KuikaListTableNameCell name={row.name} icon={<KuikaSvg name="mail" width={14} height={14} />} />
        </span>
      )
    },
    {
      id: "role",
      header: () => "Type",
      width: 180,
      accessorFn: (row: INotificationTemplateDto) => row.templateStatus,
      cell: (row: INotificationTemplateDto) => {
        const isSystem: boolean = row.templateStatus !== EMailTemplateType.Default;
        return (
          <KuikaListTableStatusCell
            label={this.getTemplateStatusLabel(row.templateStatus)}
            color={isSystem ? "blue" : "grey"}
            hint={
              isSystem
                ? "A system template. Generated apps send it by name, so it cannot be duplicated, renamed or deleted."
                : undefined
            }
          />
        );
      }
    },
    {
      id: "languages",
      header: () => "Languages",
      width: 150,
      accessorFn: (row: INotificationTemplateDto) =>
        row.multiLanguageEnabled ? (row.languageTemplates?.length ?? 0) : 0,
      cell: (row: INotificationTemplateDto) => (
        <EmailTemplateLanguagesCell
          multiLanguageEnabled={row.multiLanguageEnabled}
          languageTemplates={row.languageTemplates}
        />
      )
    },
    {
      id: "created",
      header: () => "Created",
      width: 180,
      accessorFn: (row: INotificationTemplateDto) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
      cell: (row: INotificationTemplateDto) => {
        const firstName: string = row.createdUser?.firstName ?? "";
        const lastName: string = row.createdUser?.lastName ?? "";
        const fullName: string = [firstName, lastName].filter(Boolean).join(" ");
        const initials: string = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
        return (
          <KuikaListTableUserCell
            caption={row.createdAt ? KMainFunctions.formatRelativeTime(row.createdAt) : undefined}
            name={fullName || undefined}
            photoUrl={row.createdUser?.profilePhotoUrl || undefined}
            initials={initials || undefined}
            fallback="—"
          />
        );
      }
    },
    this.getModificationColumn()
  ];

  /**
   * The shared Last modified column, with its header unsortable here. The Sort-by control and
   * the headers are one state on this page, keyed by a closed union of sort keys — a header
   * outside that union would write a key the control cannot show and the list would quietly
   * sort by something else. Sorting by when a template was last touched is what the "Recently
   * modified" tab is.
   */
  getModificationColumn = (): KuikaListTableColumn<INotificationTemplateDto> => ({
    ...buildLastModifiedColumn<INotificationTemplateDto>(
      this.state.feed.index,
      (row: INotificationTemplateDto) => row.id
    ),
    enableSorting: false
  });

  getTemplateStatusLabel = (status: EMailTemplateType): string => {
    switch (status) {
      case EMailTemplateType.ResetPassword:
        return "Reset Password";
      case EMailTemplateType.SetPassword:
        return "Set Password";
      case EMailTemplateType.EmailVerfication:
        return "Email Verification";
      case EMailTemplateType.TaskNotification:
        return "Task Notification";
      default:
        return "Custom";
    }
  };

  /** System templates offer no actions at all — the trigger itself never renders. */
  getRowActions = (): KuikaListTableRowAction<INotificationTemplateDto>[] =>
    buildEmailTemplateRowActions({ onDuplicate: this.props.onDuplicate, onRequestDelete: this.props.onRequestDelete });

  openNameModal = (templateId?: string): void => {
    this.setState({ isNameModalVisible: true, pendingTemplateId: templateId });
  };

  closeNameModal = (): void => {
    this.setState({ isNameModalVisible: false, pendingTemplateId: undefined });
  };

  /**
   * The sheet stays up through the whole create: its Create button runs, holds its ✓, and only
   * then does the canvas open the new template's editor, which replaces this page and the sheet
   * with it. It used to close on submit, before the request had even left, and hand the wait to a
   * dimmed page that did not say what it was waiting on.
   */
  handleNameSubmit = (name: string): void => {
    const templateId: string | undefined = this.state.pendingTemplateId;
    if (templateId) {
      this.props.onCreateFromTemplate(templateId, name);
    } else {
      this.props.onCreateBlank(name);
    }
  };

  handleCategoryChange = (selectedCategories: string[]): void => {
    // A narrower set starts at the top; keeping page 3 of the old set would open on
    // "Load more" with nothing above it.
    this.setState({ selectedCategories, templatePageSize: TEMPLATE_PAGE_SIZE });
  };

  renderTemplateGallery = (): React.ReactNode => {
    const searched: IEmailTemplateCatalogItem[] = filterEmailTemplates(EMAIL_TEMPLATE_CATALOG, this.state.searchText);
    // Counts come off the searched set, so a chip never promises results the search has
    // already removed, and a category the search emptied drops out of the row.
    const categories: IKuikaFilterChipOption[] = emailTemplateCategories(searched);
    const items: IEmailTemplateCatalogItem[] =
      this.state.selectedCategories.length > 0
        ? searched.filter(
            (item: IEmailTemplateCatalogItem): boolean =>
              !!item.category && this.state.selectedCategories.includes(item.category)
          )
        : searched;
    const visible: IEmailTemplateCatalogItem[] = items.slice(0, this.state.templatePageSize);
    const hasMore: boolean = items.length > visible.length;

    const chips: React.ReactNode = categories.length > 1 && (
      <KuikaFilterChips
        className="kuika-email-builder__categories"
        groupLabel="Template category"
        options={categories}
        value={this.state.selectedCategories}
        onChange={this.handleCategoryChange}
        allLabel="All"
        allCount={searched.length}
        data-testid="emailTemplateCategoryChips"
      />
    );

    if (items.length === 0) {
      return (
        <>
          {chips}
          <div className="kuika-email-builder__gallery-empty">
            <KuikaEmptyState
              variant="page"
              svgName="search-off"
              customName="No templates match your filters"
              description="Try a different search term, or clear the category filter."
            />
          </div>
        </>
      );
    }

    return (
      <>
        {chips}
        <KuikaTemplateCardGrid data-testid="emailBuilderTemplateGrid">
          {/* Creating from the catalog is the only in-flight state here; the catalog itself
              is a static import, so there is nothing to wait for on first paint. */}
          {this.props.isCreating ? (
            <KuikaTemplateCardSkeleton />
          ) : (
            visible.map((item: IEmailTemplateCatalogItem) => (
              <KuikaTemplateCard
                key={item.id}
                title={item.name}
                imageUrl={item.thumbnailUrl}
                onClick={() => this.openNameModal(item.id)}
                primaryAction={{ label: "Use template", onClick: () => this.openNameModal(item.id) }}
                data-testid={`${item.name}TemplateCard`}
              />
            ))
          )}
        </KuikaTemplateCardGrid>
        {hasMore && !this.props.isCreating && (
          <div className="kuika-email-builder__gallery-more">
            <KuikaButton
              type="secondary"
              size="md"
              data-testid="emailBuilderLoadMoreTemplates"
              onClick={() =>
                this.setState((prev: IEmailBuilderLandingState) => ({
                  templatePageSize: prev.templatePageSize + TEMPLATE_PAGE_SIZE
                }))
              }
            >
              Load more
            </KuikaButton>
          </div>
        )}
      </>
    );
  };

  getEmptyTitle = (isNoResults: boolean, isEmptyRecents: boolean): string => {
    if (isNoResults) return "No email templates found";
    if (isEmptyRecents) return "Nothing changed recently";
    return "No email templates yet";
  };

  getEmptyDescription = (isNoResults: boolean, isEmptyRecents: boolean): string => {
    if (isNoResults) return "We couldn't find any items matching your search.";
    if (isEmptyRecents) {
      // A failed feed is said out loud rather than shown as "nothing happened", which would be a
      // claim the page cannot actually make.
      return this.state.feed.status === "failed"
        ? "We couldn't load recent activity — open Emails to see every template."
        : "Emails edited in the last 30 days appear here.";
    }
    return "No email templates have been added yet.";
  };

  /** Takes the tab `render` already resolved: deciding it again here would re-sort the list. */
  renderTable = (activeTab: string): React.ReactNode => {
    const isRecentTab: boolean = activeTab === RECENTLY_MODIFIED_TAB;
    const rows: INotificationTemplateDto[] = isRecentTab ? this.getRecentTemplates() : this.getFilteredTemplates();
    const hasTemplates: boolean = this.getEmailTemplates().length > 0;
    const isSearching: boolean = this.state.searchText.length > 0;
    // The "nothing here yet" state wins over the search miss, as it did in the panel.
    const isNoResults: boolean = hasTemplates && isSearching;
    // The recent tab is only reached with nothing in it when the user picked it deliberately,
    // or when a search emptied it — the page opens on the Emails list otherwise.
    const isEmptyRecents: boolean = isRecentTab && rows.length === 0 && !isSearching;
    // The page parks on the recent tab while the feed is in flight. Without this it would paint
    // an empty table first and claim nothing had changed recently — something it cannot yet know.
    const isFeedPending: boolean = isRecentTab && this.state.feed.status === "loading";

    return (
      <KuikaListTable<INotificationTemplateDto>
        data={rows}
        loading={isFeedPending && rows.length === 0}
        columns={this.getColumns()}
        rowActions={this.getRowActions()}
        // The id the Emails list has always emitted for a row's menu, kept so the E2E
        // suite still targets one specific template's actions.
        rowActionsTestId={(row: INotificationTemplateDto) => `${row.name}ThreeDotMenuButton`}
        rowKey={(row: INotificationTemplateDto) => row.id}
        revealRowKey={this.props.revealTemplateId}
        onRowRevealed={this.props.onTemplateRevealed}
        isRowBusy={(row: INotificationTemplateDto) => row.id === this.props.duplicatingId}
        onRowClick={(row: INotificationTemplateDto) => this.props.onOpenTemplate(row)}
        emptyIcon={
          <KuikaSvg
            name={isNoResults ? "search-off" : "email-builder"}
            type={isNoResults ? "common" : "topbar-icons"}
            width={32}
            height={32}
          />
        }
        // The recent tab is the feed's order; its headers offer no sort of their own, the same
        // way the Sort-by control steps aside for it.
        disableSorting={isRecentTab}
        sorting={this.getSortingState()}
        onSortingChange={this.handleSortingChange}
        manualSorting
        emptyVariant="page"
        emptyTitle={this.getEmptyTitle(isNoResults, isEmptyRecents)}
        emptyDescription={this.getEmptyDescription(isNoResults, isEmptyRecents)}
        data-testid="emailBuilderTable"
      />
    );
  };

  render() {
    const activeTab: string = this.getActiveTab();
    const isTemplatesTab: boolean = activeTab === TEMPLATES_TAB;

    return (
      <>
        <KuikaListPage className="kuika-email-builder" data-testid="emailBuilderScreen">
          {/* Named after the page's job — managing the emails you already have — not after
              the create cards, which are one element within it. */}
          <KuikaListPageHeader
            icon={<KuikaSvg name="email-builder" type="topbar-icons" width={20} height={20} />}
            title="Email Builder"
            titleTestId="emailBuilderTitle"
            // The Emails panel's header carried these two anchors; onboarding tours are
            // authored in the backend against them, and a step whose target never mounts
            // silently mis-anchors rather than erroring. They move here with the list.
            renderTitle={(title: string) => (
              <KuikaH4 data-testid="emailBuilderTitle" data-onboarding-id="searchable-panel-header-name-emails">
                {title}
              </KuikaH4>
            )}
          />

          <KuikaCreateCardRow>
            <KuikaCreateCard
              title="Create a blank email"
              description="Start from an empty canvas and build the layout block by block."
              icon={<KuikaSvg name="plus" width={20} height={20} />}
              iconBackground="var(--blue-1)"
              iconColor="var(--accent-fg)"
              onClick={() => this.openNameModal(undefined)}
              loading={this.props.isCreating}
              data-testid="newBlankTemplateButton"
              data-onboarding-id="left-panel_email-builder_plus"
            />
            <KuikaCreateCard
              title="From template"
              description="Pick a ready-made design from hundreds of free email templates."
              icon={<KuikaSvg name="layout-rows" width={20} height={20} />}
              iconBackground="var(--purple-1)"
              iconColor="var(--purple-5)"
              onClick={() => this.setState({ pickedTab: TEMPLATES_TAB })}
              data-testid="emailBuilderFromTemplateCard"
            />
          </KuikaCreateCardRow>

          <KuikaListPageToolbar
            tabs={
              <KuikaTabs
                size="lg"
                options={[
                  {
                    value: RECENTLY_MODIFIED_TAB,
                    label: RECENTLY_MODIFIED_TAB,
                    dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID
                  },
                  { value: EMAILS_TAB, label: EMAILS_TAB, dataTestId: "emailsTab" },
                  { value: TEMPLATES_TAB, label: TEMPLATES_TAB, dataTestId: "emailTemplatesTab" }
                ]}
                value={activeTab}
                onChange={(value: string) => this.setState({ pickedTab: value })}
                dataTestId="emailBuilderTabs"
              />
            }
            search={
              <KuikaInput
                size="md"
                placeholder="Search by name..."
                value={this.state.searchText}
                leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                  this.handleSearchChange(String(value))
                }
                data-testid="searchInput"
                data-onboarding-id="searchable-panel-header-emails"
                // The panel header this replaced offered one-click clearing; the toolbar
                // input keeps it rather than making the user select and delete.
                rightIcon={
                  this.state.searchText ? (
                    <button
                      type="button"
                      className="kuika-email-builder__search-clear"
                      aria-label="Clear search"
                      data-testid="clearSearchInputButton"
                      onClick={() => this.handleSearchChange("")}
                    >
                      <KuikaSvg name="x" width={12} height={12} />
                    </button>
                  ) : undefined
                }
              />
            }
            filters={
              // Only the Emails tab has an order of its own to choose: the catalog is the
              // catalog's order, and the recent tab is the feed's.
              activeTab !== EMAILS_TAB ? undefined : (
                <KuikaSelectbox
                  className="kuika-email-builder__sort-filter"
                  size="md"
                  placeholder="Sort by"
                  allowClear
                  value={this.state.sort}
                  options={SORT_OPTIONS}
                  prefix={<KuikaSvg name="sort-descending" width={12} height={12} />}
                  onChange={(value: string | null) => this.setState({ sort: (value as EmailTemplateSortKey) ?? null })}
                  data-testid="emailBuilderSortFilter"
                />
              )
            }
          />

          {isTemplatesTab ? this.renderTemplateGallery() : this.renderTable(activeTab)}
        </KuikaListPage>

        <EmailTemplateNameModal
          visible={this.state.isNameModalVisible}
          title={this.state.pendingTemplateId ? "Create from template" : "Create a blank email"}
          subtitle={
            this.state.pendingTemplateId
              ? "Your copy starts from the template you picked — rename it any time."
              : "Start from an empty canvas and build the email yourself."
          }
          iconName={this.state.pendingTemplateId ? "template" : "mail"}
          confirmLabel="Create"
          status={this.props.createStatus}
          templates={this.props.sd?.notificationTemplates ?? []}
          onCancel={this.closeNameModal}
          onSubmit={this.handleNameSubmit}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const emailBuilderLanding = withSD<IEmailBuilderLandingProps>(EmailBuilderLanding);
export { emailBuilderLanding as EmailBuilderLanding };
